import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { parseInstant } from "../../utils/dateUtils";
import { adminService } from "../../features/admin/service";
import type { CollectionStat, SystemHealth, S3BackupStatus } from "../../features/admin/types";
import AdminWebSocketHealthPage from "./AdminWebSocketHealthPage";
import AdminRecRedisHealthPage from "./AdminRecRedisHealthPage";
import RefreshButton from "../../components/admin/RefreshButton";

const AdminSettingsPage: React.FC = () => {
  // Each section below fetches independently on mount instead of behind one
  // shared Promise.all — a single slow/hung endpoint (e.g. the Mongo
  // collection-stats scan) used to stick the whole page behind one spinner.
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [collections, setCollections] = useState<CollectionStat[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [retention, setRetention] = useState<{
    session_days: number;
    batch_size: number;
    updated_by: string | null;
    updated_at: string | null;
  } | null>(null);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionForm, setRetentionForm] = useState({ session_days: 0, batch_size: 100 });
  const [retentionSaving, setRetentionSaving] = useState(false);

  type GateRole = "seller" | "buyer";
  type GateFeature = "messaging" | "enquiry" | "deal_creation" | "enquiry_receive" | "ai_chat" | "webinar";
  const GATE_FEATURES: Record<GateRole, GateFeature[]> = {
    seller: ["messaging", "deal_creation", "enquiry", "enquiry_receive", "ai_chat", "webinar"],
    buyer: ["messaging", "enquiry", "ai_chat", "webinar"],
  };
  const GATE_LABELS: Record<GateFeature, string> = {
    messaging: "Messaging (DM threads + sending messages)",
    enquiry: "Enquiries — sending (draft, send, and Deal AI chat's finalize/Yes-No send)",
    deal_creation: "Deal creation",
    enquiry_receive: "Enquiries — receiving (seller inbox)",
    ai_chat: "Deal AI reply chat (Get Details popup only) — finalize/send is gated by Enquiries above instead",
    webinar: "Hosting webinars",
  };
  // Rendered row order — kept separate from GATE_FEATURES (which drives
  // per-role applicability) so the two new gates land grouped with their
  // related row instead of alphabetically.
  const GATE_ROWS: GateFeature[] = ["messaging", "enquiry", "enquiry_receive", "ai_chat", "deal_creation", "webinar"];
  const [gates, setGates] = useState<{
    seller: Partial<Record<GateFeature, boolean>>;
    buyer: Partial<Record<GateFeature, boolean>>;
    // Separate, unrelated gate — NOT tied to is_verified. Whether Get
    // Details AI chat / flyer generation / market research require
    // gst_number + website to be filled in, per role.
    gst_website_required?: { seller?: boolean; buyer?: boolean };
    updated_at: string | null;
  } | null>(null);
  const [gatesLoading, setGatesLoading] = useState(true);
  const [gateSavingKey, setGateSavingKey] = useState<string | null>(null);

  // Separate from Verification Gating above — only governs whether declining
  // (No / X) the deal AI chat confirm dialog still silently sends the
  // enquiry to the seller. Yes is unaffected and always goes through the
  // Enquiries verification gate instead.
  const [declineBehavior, setDeclineBehavior] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [declineBehaviorLoading, setDeclineBehaviorLoading] = useState(true);
  const [declineBehaviorSaving, setDeclineBehaviorSaving] = useState(false);

  const [pricingSettings, setPricingSettings] = useState<Record<string, { slug: string; name: string; group_slug: string | null; pricing_required: boolean }[]> | null>(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingSavingSlug, setPricingSavingSlug] = useState<string | null>(null);

  const [gstVerification, setGstVerification] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [gstLoading, setGstLoading] = useState(true);
  const [gstSaving, setGstSaving] = useState(false);

  const [verificationEmail, setVerificationEmail] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [verificationEmailLoading, setVerificationEmailLoading] = useState(true);
  const [verificationEmailSaving, setVerificationEmailSaving] = useState(false);

  const [transactionalEmail, setTransactionalEmail] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [transactionalEmailLoading, setTransactionalEmailLoading] = useState(true);
  const [transactionalEmailSaving, setTransactionalEmailSaving] = useState(false);

  const [phoneOtp, setPhoneOtp] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(true);
  const [phoneOtpSaving, setPhoneOtpSaving] = useState(false);

  const [uploadCleanup, setUploadCleanup] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [uploadCleanupLoading, setUploadCleanupLoading] = useState(true);
  const [uploadCleanupSaving, setUploadCleanupSaving] = useState(false);

  const [askFileReading, setAskFileReading] = useState<{ enabled: boolean; updated_by: string | null; updated_at: string | null } | null>(null);
  const [askFileReadingLoading, setAskFileReadingLoading] = useState(true);
  const [askFileReadingSaving, setAskFileReadingSaving] = useState(false);

  const [s3Backup, setS3Backup] = useState<S3BackupStatus | null>(null);
  const [s3BackupLoading, setS3BackupLoading] = useState(true);
  const [s3BackupTriggering, setS3BackupTriggering] = useState(false);

  const fetchAllSettings = useCallback(() => {
    return Promise.all([
      adminService.getSystemHealth()
        .then(setHealth)
        .catch((err) => console.error(err))
        .finally(() => setHealthLoading(false)),

      adminService.getCollectionStats()
        .then(setCollections)
        .catch((err) => console.error(err))
        .finally(() => setCollectionsLoading(false)),

      adminService.getRagRetention()
        .then((ret) => {
          setRetention(ret);
          setRetentionForm({ session_days: ret.session_days, batch_size: ret.batch_size ?? 100 });
        })
        .catch((err) => console.error(err))
        .finally(() => setRetentionLoading(false)),

      adminService.getVerificationGates()
        .then(setGates)
        .catch((err) => console.error(err))
        .finally(() => setGatesLoading(false)),

      adminService.getSilentEnquiryOnDecline()
        .then(setDeclineBehavior)
        .catch((err) => console.error(err))
        .finally(() => setDeclineBehaviorLoading(false)),

      adminService.getPricingSettings()
        .then((res) => setPricingSettings(res.groups))
        .catch((err) => console.error(err))
        .finally(() => setPricingLoading(false)),

      adminService.getGstVerification()
        .then(setGstVerification)
        .catch((err) => console.error(err))
        .finally(() => setGstLoading(false)),

      adminService.getVerificationEmail()
        .then(setVerificationEmail)
        .catch((err) => console.error(err))
        .finally(() => setVerificationEmailLoading(false)),

      adminService.getTransactionalEmail()
        .then(setTransactionalEmail)
        .catch((err) => console.error(err))
        .finally(() => setTransactionalEmailLoading(false)),

      adminService.getPhoneOtp()
        .then(setPhoneOtp)
        .catch((err) => console.error(err))
        .finally(() => setPhoneOtpLoading(false)),

      adminService.getUploadCleanup()
        .then(setUploadCleanup)
        .catch((err) => console.error(err))
        .finally(() => setUploadCleanupLoading(false)),

      adminService.getAskFileReading()
        .then(setAskFileReading)
        .catch((err) => console.error(err))
        .finally(() => setAskFileReadingLoading(false)),

      adminService.getS3BackupStatus()
        .then(setS3Backup)
        .catch((err) => console.error(err))
        .finally(() => setS3BackupLoading(false)),
    ]).then(() => {});
  }, []);

  useEffect(() => {
    fetchAllSettings();
  }, [fetchAllSettings]);

  // Sync runs server-side in a background task -- poll while "running" so
  // the tab reflects completion without a manual refresh.
  useEffect(() => {
    if (s3Backup?.state !== "running") return;
    const interval = setInterval(() => {
      adminService.getS3BackupStatus().then(setS3Backup).catch((err) => console.error(err));
    }, 5000);
    return () => clearInterval(interval);
  }, [s3Backup?.state]);

  const triggerS3Backup = async () => {
    setS3BackupTriggering(true);
    try {
      const { status: newStatus } = await adminService.triggerS3Backup();
      setS3Backup(newStatus);
    } catch (err: any) {
      // 409 = already running -- backend returns the current status in the error body
      const existing = err?.response?.data?.detail?.status;
      if (existing) setS3Backup(existing);
      else console.error(err);
    } finally {
      setS3BackupTriggering(false);
    }
  };

  const toggleGstVerification = async () => {
    if (!gstVerification) return;
    setGstSaving(true);
    try {
      const updated = await adminService.updateGstVerification(!gstVerification.enabled);
      setGstVerification(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setGstSaving(false);
    }
  };

  const toggleVerificationEmail = async () => {
    if (!verificationEmail) return;
    setVerificationEmailSaving(true);
    try {
      const updated = await adminService.updateVerificationEmail(!verificationEmail.enabled);
      setVerificationEmail(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setVerificationEmailSaving(false);
    }
  };

  const toggleTransactionalEmail = async () => {
    if (!transactionalEmail) return;
    setTransactionalEmailSaving(true);
    try {
      const updated = await adminService.updateTransactionalEmail(!transactionalEmail.enabled);
      setTransactionalEmail(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setTransactionalEmailSaving(false);
    }
  };

  const togglePhoneOtp = async () => {
    if (!phoneOtp) return;
    setPhoneOtpSaving(true);
    try {
      const updated = await adminService.updatePhoneOtp(!phoneOtp.enabled);
      setPhoneOtp(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setPhoneOtpSaving(false);
    }
  };

  const toggleAskFileReading = async () => {
    if (!askFileReading) return;
    setAskFileReadingSaving(true);
    try {
      const updated = await adminService.updateAskFileReading(!askFileReading.enabled);
      setAskFileReading(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setAskFileReadingSaving(false);
    }
  };

  const toggleUploadCleanup = async () => {
    if (!uploadCleanup) return;
    setUploadCleanupSaving(true);
    try {
      const updated = await adminService.updateUploadCleanup(!uploadCleanup.enabled);
      setUploadCleanup(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setUploadCleanupSaving(false);
    }
  };

  const toggleDeclineBehavior = async () => {
    if (!declineBehavior) return;
    setDeclineBehaviorSaving(true);
    try {
      const updated = await adminService.updateSilentEnquiryOnDecline(!declineBehavior.enabled);
      setDeclineBehavior(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setDeclineBehaviorSaving(false);
    }
  };

  const toggleGate = async (role: GateRole, feature: GateFeature) => {
    if (!gates) return;
    const key = `${role}.${feature}`;
    const nextValue = !gates[role]?.[feature];
    setGateSavingKey(key);
    try {
      const updated = await adminService.updateVerificationGates({ [role]: { [feature]: nextValue } } as any);
      setGates(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setGateSavingKey(null);
    }
  };

  const toggleGstWebsiteGate = async (role: GateRole) => {
    if (!gates) return;
    const key = `gst_website_required.${role}`;
    const nextValue = !gates.gst_website_required?.[role];
    setGateSavingKey(key);
    try {
      const updated = await adminService.updateVerificationGates({ gst_website_required: { [role]: nextValue } } as any);
      setGates(updated);
    } catch (err) {
      console.error(err);
    } finally {
      setGateSavingKey(null);
    }
  };

  const togglePricingRequired = async (slug: string, current: boolean) => {
    if (!pricingSettings) return;
    const nextValue = !current;
    const previous = pricingSettings;
    setPricingSavingSlug(slug);
    setPricingSettings((prev) => {
      if (!prev) return prev;
      const next: typeof prev = {};
      for (const [group, cats] of Object.entries(prev)) {
        next[group] = cats.map((cat) => (cat.slug === slug ? { ...cat, pricing_required: nextValue } : cat));
      }
      return next;
    });
    try {
      await adminService.updatePricingRequired(slug, nextValue);
    } catch (err) {
      console.error(err);
      setPricingSettings(previous);
    } finally {
      setPricingSavingSlug(null);
    }
  };

  const saveRetention = async () => {
    setRetentionSaving(true);
    try {
      const data = await adminService.updateRagRetention(retentionForm);
      setRetention({ session_days: retentionForm.session_days, batch_size: retentionForm.batch_size, updated_by: data.updated_by, updated_at: data.updated_at ?? new Date().toISOString() });
    } catch (e) {
      console.error(e);
    } finally {
      setRetentionSaving(false);
    }
  };

  const totalDocs = collections.reduce((acc, c) => acc + c.documents, 0);
  const sectionSpinner = (
    <div className="flex items-center justify-center py-10">
      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  type TabKey = "health" | "gating" | "pricing" | "email" | "retention" | "s3backup" | "websocket" | "recRedisHealth";
  const TABS: { key: TabKey; label: string }[] = [
    { key: "health", label: "Health & Database" },
    { key: "gating", label: "Verification Gating" },
    { key: "pricing", label: "Pricing" },
    { key: "email", label: "Email Notifications" },
    { key: "retention", label: "Data Retention" },
    { key: "s3backup", label: "S3 Backup" },
    { key: "websocket", label: "WebSocket Health" },
    { key: "recRedisHealth", label: "Rec Redis Health" },
  ];
  const [activeTab, setActiveTab] = useState<TabKey>("health");

  return (
    <div>
      <AdminPageHeader
        title="Settings & System"
        subtitle="System health, verification rules, notifications, and retention."
        actions={<RefreshButton onRefresh={fetchAllSettings} />}
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-800 mb-8">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key
                ? "border-indigo-500 text-white"
                : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "health" && (
      <>
      {/* Health Status */}
      {healthLoading ? sectionSpinner : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">System Status</p>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${health?.status === "healthy" ? "bg-green-400" : "bg-amber-400"}`} />
            <span className={`text-lg font-semibold ${health?.status === "healthy" ? "text-green-400" : "text-amber-400"}`}>
              {health?.status?.toUpperCase() || "UNKNOWN"}
            </span>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">Database</p>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${health?.database === "connected" ? "bg-green-400" : "bg-red-400"}`} />
            <span className="text-lg font-semibold text-white">{health?.database || "unknown"}</span>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">Redis</p>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${health?.redis === "connected" ? "bg-green-400" : "bg-red-400"}`} />
            <span className="text-lg font-semibold text-white">{health?.redis || "unknown"}</span>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">Version</p>
          <span className="text-lg font-semibold text-white">{health?.version || "—"}</span>
        </div>
      </div>
      )}

      {/* Database Collections */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Database Collections</h2>
          {!collectionsLoading && (
            <span className="text-sm text-gray-500">{collections.length} collections, {totalDocs.toLocaleString()} documents</span>
          )}
        </div>
        {collectionsLoading ? sectionSpinner : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-6 text-gray-500 font-medium">Collection</th>
                <th className="text-right py-3 px-6 text-gray-500 font-medium">Documents</th>
                <th className="text-right py-3 px-6 text-gray-500 font-medium">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {collections.map((c) => (
                <tr key={c.collection} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                  <td className="py-3 px-6">
                    <code className="text-indigo-400 text-xs bg-indigo-500/10 px-2 py-1 rounded">{c.collection}</code>
                  </td>
                  <td className="py-3 px-6 text-right text-white font-medium">{c.documents.toLocaleString()}</td>
                  <td className="py-3 px-6 text-right text-gray-500">
                    {totalDocs > 0 ? ((c.documents / totalDocs) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
      </>
      )}

      {activeTab === "gating" && (
      <>
      {/* Verification Gating */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Verification Gating</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control which features are blocked for a seller/buyer whose account isn't yet admin-verified.
          Turning a toggle off lets unverified users use that feature immediately; turning it on blocks
          them until an admin verifies their account. Each toggle enforces against whichever role is
          actually calling — e.g. Deal AI chat / enquiries are reachable by a seller enquiring on another
          seller's deal, not just buyers, and the Seller column is checked for that caller too.
        </p>
        {gatesLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-6 text-gray-500 font-medium">Feature</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Seller</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Buyer</th>
              </tr>
            </thead>
            <tbody>
              {GATE_ROWS.map((feature) => (
                <tr key={feature} className="border-b border-gray-800/50">
                  <td className="py-3 px-6 text-gray-300">{GATE_LABELS[feature]}</td>
                  {(["seller", "buyer"] as GateRole[]).map((role) => {
                    const applicable = GATE_FEATURES[role].includes(feature);
                    const key = `${role}.${feature}`;
                    const enabled = !!gates?.[role]?.[feature];
                    return (
                      <td key={role} className="py-3 px-6 text-center">
                        {!applicable ? (
                          <span className="text-gray-600 text-xs">N/A</span>
                        ) : (
                          <button
                            onClick={() => toggleGate(role, feature)}
                            disabled={gateSavingKey === key}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                              enabled ? "bg-indigo-600" : "bg-gray-700"
                            }`}
                            aria-label={`${enabled ? "Disable" : "Enable"} verification requirement for ${role} ${feature}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                enabled ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        {gates?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">Last updated: {(parseInstant(gates.updated_at) ?? new Date(gates.updated_at)).toLocaleString()}</p>
        )}
        </>
        )}
      </div>

      {/* GST / website completeness gate — separate from Verification Gating
          above: NOT tied to is_verified, just whether the field data exists. */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-white mb-1">GST / Website Required for AI Features</h2>
        <p className="text-sm text-gray-500 mb-4">
          Whether Get Details AI chat, flyer generation, and market research require GST number + company
          website to be filled in, per role. Independent of admin verification above — this only checks
          whether the two fields are present. Turning a toggle off lets that role use these features even
          with GST/website missing.
        </p>
        {gatesLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-6 text-gray-500 font-medium">Feature</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Seller</th>
                <th className="text-center py-3 px-6 text-gray-500 font-medium">Buyer</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-6 text-gray-300">GST / company website required</td>
                {(["seller", "buyer"] as GateRole[]).map((role) => {
                  const key = `gst_website_required.${role}`;
                  const enabled = gates?.gst_website_required?.[role] ?? true;
                  return (
                    <td key={role} className="py-3 px-6 text-center">
                      <button
                        onClick={() => toggleGstWebsiteGate(role)}
                        disabled={gateSavingKey === key}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                          enabled ? "bg-indigo-600" : "bg-gray-700"
                        }`}
                        aria-label={`${enabled ? "Disable" : "Enable"} GST/website requirement for ${role}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            enabled ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
          </div>
        </div>
        {gates?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">Last updated: {(parseInstant(gates.updated_at) ?? new Date(gates.updated_at)).toLocaleString()}</p>
        )}
        </>
        )}
      </div>

      {/* Enquiry decline behavior — separate from Verification Gating above */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-white mb-1">Enquiry on Decline (No/Close)</h2>
        <p className="text-sm text-gray-500 mb-4">
          Not a verification setting — this only decides what happens when a buyer clicks No / closes the
          deal AI chat confirm dialog. "Yes" is never affected by this toggle; it always goes through the
          Enquiries verification gate above instead.
        </p>
        {declineBehaviorLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-lg flex items-center justify-between">
          <span className="text-gray-300 text-sm">
            Declining {declineBehavior?.enabled ? "still silently sends the enquiry to the seller" : "sends nothing at all"}
          </span>
          <button
            onClick={toggleDeclineBehavior}
            disabled={declineBehaviorSaving || !declineBehavior}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ml-4 ${
              declineBehavior?.enabled ? "bg-indigo-600" : "bg-gray-700"
            }`}
            aria-label={`${declineBehavior?.enabled ? "Disable" : "Enable"} silent enquiry on decline`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                declineBehavior?.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {declineBehavior?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">
            Last updated: {(parseInstant(declineBehavior.updated_at) ?? new Date(declineBehavior.updated_at)).toLocaleString()}
          </p>
        )}
        </>
        )}
      </div>

      {/* GST Verification */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-white mb-1">GST Verification</h2>
        <p className="text-sm text-gray-500 mb-4">
          When enabled, GSTIN values entered during onboarding/profile updates are verified against the
          GST portal. When disabled, GST numbers are saved without verification.
        </p>
        {gstLoading ? sectionSpinner : (
        <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-lg flex items-center justify-between">
          <span className="text-gray-300 text-sm">GST verification is {gstVerification?.enabled ? "started" : "stopped"}</span>
          <button
            onClick={toggleGstVerification}
            disabled={gstSaving || !gstVerification}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
              gstVerification?.enabled ? "bg-indigo-600" : "bg-gray-700"
            }`}
            aria-label={`${gstVerification?.enabled ? "Disable" : "Enable"} GST verification`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                gstVerification?.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {gstVerification?.updated_at && (
          <p className="text-xs text-gray-500 mt-2">
            Last updated: {(parseInstant(gstVerification.updated_at) ?? new Date(gstVerification.updated_at)).toLocaleString()}
          </p>
        )}
        </>
        )}
      </div>
      </>
      )}

      {activeTab === "pricing" && (
      <>
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Pricing</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control which categories require sellers to enter pricing when creating a deal. Turning a
          toggle off makes pricing optional for that category.
        </p>
        {pricingLoading ? sectionSpinner : (
        <>
        {Object.entries(pricingSettings ?? {}).map(([groupSlug, categories]) => (
          <div key={groupSlug} className="mb-6">
            <h3 className="text-sm font-semibold text-gray-400 mb-2 capitalize">{groupSlug || "Ungrouped"}</h3>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-3 px-6 text-gray-500 font-medium">Category</th>
                    <th className="text-center py-3 px-6 text-gray-500 font-medium">Pricing required</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.slug} className="border-b border-gray-800/50">
                      <td className="py-3 px-6 text-gray-300">{cat.name}</td>
                      <td className="py-3 px-6 text-center">
                        <button
                          onClick={() => togglePricingRequired(cat.slug, cat.pricing_required)}
                          disabled={pricingSavingSlug === cat.slug}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                            cat.pricing_required ? "bg-indigo-600" : "bg-gray-700"
                          }`}
                          aria-label={`${cat.pricing_required ? "Disable" : "Enable"} pricing requirement for ${cat.name}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              cat.pricing_required ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        ))}
        </>
        )}
      </div>
      </>
      )}

      {activeTab === "email" && (
      <>
      {/* Email Notifications */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Email Notifications</h2>
        <p className="text-sm text-gray-500 mb-4">
          Control which transactional emails the platform sends. Both default to on.
        </p>
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden max-w-2xl divide-y divide-gray-800">
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Verification emails</p>
              <p className="text-xs text-gray-500 mt-1">
                Admin alert when a user completes onboarding, and the "you're verified" email sent to a
                buyer/seller once an admin approves them.
              </p>
              {verificationEmailLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {verificationEmail?.enabled ? "on" : "off"}
                  {verificationEmail?.updated_at && (
                    <> · Last updated: {(parseInstant(verificationEmail.updated_at) ?? new Date(verificationEmail.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {verificationEmailLoading ? sectionSpinner : (
              <button
                onClick={toggleVerificationEmail}
                disabled={verificationEmailSaving || !verificationEmail}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  verificationEmail?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${verificationEmail?.enabled ? "Disable" : "Enable"} verification emails`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    verificationEmail?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Transactional emails</p>
              <p className="text-xs text-gray-500 mt-1">
                Welcome, forgot-password, password-reset-confirmation, and profile email-verification
                emails. Leave this on — turning it off can lock users out of password reset or block
                them from verifying their email.
              </p>
              {transactionalEmailLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {transactionalEmail?.enabled ? "on" : "off"}
                  {transactionalEmail?.updated_at && (
                    <> · Last updated: {(parseInstant(transactionalEmail.updated_at) ?? new Date(transactionalEmail.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {transactionalEmailLoading ? sectionSpinner : (
              <button
                onClick={toggleTransactionalEmail}
                disabled={transactionalEmailSaving || !transactionalEmail}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  transactionalEmail?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${transactionalEmail?.enabled ? "Disable" : "Enable"} transactional emails`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    transactionalEmail?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Phone / WhatsApp OTP</p>
              <p className="text-xs text-gray-500 mt-1">
                Real WhatsApp verification codes for signup, login, OAuth new-account setup, and profile
                phone changes. Turning this off puts the platform into test mode: every phone OTP anywhere
                becomes the fixed code <code className="text-indigo-400">0000</code> and no real WhatsApp
                message is sent. Use only in non-production environments.
              </p>
              {phoneOtpLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {phoneOtp?.enabled ? "on" : "off (test mode — code is 0000)"}
                  {phoneOtp?.updated_at && (
                    <> · Last updated: {(parseInstant(phoneOtp.updated_at) ?? new Date(phoneOtp.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {phoneOtpLoading ? sectionSpinner : (
              <button
                onClick={togglePhoneOtp}
                disabled={phoneOtpSaving || !phoneOtp}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  phoneOtp?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${phoneOtp?.enabled ? "Disable" : "Enable"} phone/WhatsApp OTP`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    phoneOtp?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">Delete unused uploads automatically</p>
              <p className="text-xs text-gray-500 mt-1">
                Files users attach in Flyers, Videos, Itinerary and Ask Deal but never use are removed from
                storage: every 10 minutes the server deletes files that have sat unused for 3 hours (Ask Deal
                files that were never sent, too). <strong>On:</strong> private documents such as ID scans do not
                pile up, and storage cost stays low. <strong>Off:</strong> nothing is deleted automatically, so
                unused files stay in storage until you switch it back on. Files already used in a generation or a
                sent message are never deleted, and no user sees any difference either way. Leaving a page
                still removes the files the user just dropped.
              </p>
              {uploadCleanupLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {uploadCleanup?.enabled ? "on" : "off (unused uploads are kept)"}
                  {uploadCleanup?.updated_at && (
                    <> · Last updated: {(parseInstant(uploadCleanup.updated_at) ?? new Date(uploadCleanup.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {uploadCleanupLoading ? sectionSpinner : (
              <button
                onClick={toggleUploadCleanup}
                disabled={uploadCleanupSaving || !uploadCleanup}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  uploadCleanup?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${uploadCleanup?.enabled ? "Disable" : "Enable"} automatic deletion of unused uploads`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    uploadCleanup?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="pr-4">
              <p className="text-gray-300 text-sm font-medium">AI reading of Ask Deal files</p>
              <p className="text-xs text-gray-500 mt-1">
                When a user attaches an itinerary, quote or ticket in Ask Deal, an AI model reads it (a paid call,
                limited per user per day). <strong>On:</strong> files are read as normal. <strong>Off:</strong> no file
                is read and no AI call is made; the user's message still goes through and they see "Reading files is
                switched off right now". Files already read stay in their chats; files not yet read are read normally
                once you switch it back on. Takes effect immediately.
              </p>
              {askFileReadingLoading ? null : (
                <p className="text-xs text-gray-500 mt-1">
                  Currently {askFileReading?.enabled ? "on" : "off (files are not read)"}
                  {askFileReading?.updated_at && (
                    <> · Last updated: {(parseInstant(askFileReading.updated_at) ?? new Date(askFileReading.updated_at)).toLocaleString()}</>
                  )}
                </p>
              )}
            </div>
            {askFileReadingLoading ? sectionSpinner : (
              <button
                onClick={toggleAskFileReading}
                disabled={askFileReadingSaving || !askFileReading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                  askFileReading?.enabled ? "bg-indigo-600" : "bg-gray-700"
                }`}
                aria-label={`${askFileReading?.enabled ? "Disable" : "Enable"} AI reading of Ask Deal files`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    askFileReading?.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      </>
      )}

      {activeTab === "retention" && (
      <>
      {/* Data Retention */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Data Retention</h2>
        {retentionLoading ? sectionSpinner : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4 max-w-lg">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Chat sessions TTL (days)</label>
            <input
              type="number" min={0}
              value={retentionForm.session_days}
              onChange={e => setRetentionForm(f => ({ ...f, session_days: Number(e.target.value) }))}
              className="w-48 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              0 = never archive. Sessions older than this are moved to archive and removed from the main collection.
            </p>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Batch size (sessions per hourly sweep)</label>
            <input
              type="number" min={1} max={5000}
              value={retentionForm.batch_size}
              onChange={e => setRetentionForm(f => ({ ...f, batch_size: Number(e.target.value) }))}
              className="w-48 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
            <p className="text-xs text-gray-500 mt-1">How many sessions to archive per hourly run. Increase to clear backlog faster.</p>
          </div>
          {retention?.updated_at && (
            <p className="text-xs text-gray-500">Last saved: {(parseInstant(retention.updated_at) ?? new Date(retention.updated_at)).toLocaleString()}{retention.updated_by ? ` by ${retention.updated_by}` : ""}</p>
          )}
          <button
            onClick={saveRetention}
            disabled={retentionSaving}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {retentionSaving ? "Saving..." : "Save Retention Settings"}
          </button>
        </div>
        )}
      </div>
      </>
      )}

      {activeTab === "s3backup" && (
      <>
      {/* S3 Backup */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">S3 Backup</h2>
        <p className="text-sm text-gray-500 mb-4">
          New uploads are already backed up automatically and continuously via AWS S3 Replication
          (configured at the bucket level, outside this app — nothing to trigger for that). This button
          is a supplement: an on-demand sync you can run any time, e.g. right before a risky deploy, or to
          catch anything replication might have missed. It's incremental, so running it repeatedly only
          copies what's changed since the last run.
        </p>
        {s3BackupLoading ? sectionSpinner : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-2xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  s3Backup?.state === "success" ? "bg-green-400" :
                  s3Backup?.state === "running" ? "bg-amber-400 animate-pulse" :
                  s3Backup?.state === "failed" ? "bg-red-400" : "bg-gray-600"
                }`}
              />
              <span className="text-white font-medium">
                {s3Backup?.state === "running" ? "Sync in progress…" :
                 s3Backup?.state === "success" ? "Last sync succeeded" :
                 s3Backup?.state === "failed" ? "Last sync failed" : "No sync run yet"}
              </span>
            </div>
            <button
              onClick={triggerS3Backup}
              disabled={s3BackupTriggering || s3Backup?.state === "running"}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {s3Backup?.state === "running" ? "Running…" : s3BackupTriggering ? "Starting…" : "Run Backup Now"}
            </button>
          </div>

          {(s3Backup?.state === "success" || s3Backup?.state === "failed") && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Scanned</p>
                <p className="text-white font-medium">{s3Backup.scanned ?? "—"}</p>
              </div>
              <div>
                <p className="text-gray-500">Copied</p>
                <p className="text-white font-medium">{s3Backup.copied ?? "—"}</p>
              </div>
              <div>
                <p className="text-gray-500">Skipped (unchanged)</p>
                <p className="text-white font-medium">{s3Backup.skipped ?? "—"}</p>
              </div>
              <div>
                <p className="text-gray-500">Errors</p>
                <p className={`font-medium ${s3Backup.errors ? "text-red-400" : "text-white"}`}>{s3Backup.errors ?? "—"}</p>
              </div>
            </div>
          )}

          {s3Backup?.error && (
            <p className="text-xs text-red-400 bg-red-500/10 rounded-lg p-3">{s3Backup.error}</p>
          )}
          {!!s3Backup?.error_samples?.length && (
            <div className="text-xs text-red-400 bg-red-500/10 rounded-lg p-3 space-y-1">
              {s3Backup.error_samples.map((e, i) => <p key={i}>{e}</p>)}
            </div>
          )}

          {(s3Backup?.started_at || s3Backup?.finished_at) && (
            <p className="text-xs text-gray-500">
              {s3Backup.started_at && <>Started: {(parseInstant(s3Backup.started_at) ?? new Date(s3Backup.started_at)).toLocaleString()}</>}
              {s3Backup.finished_at && <> · Finished: {(parseInstant(s3Backup.finished_at) ?? new Date(s3Backup.finished_at)).toLocaleString()}</>}
            </p>
          )}
        </div>
        )}
      </div>
      </>
      )}

      {activeTab === "websocket" && <AdminWebSocketHealthPage />}
      {activeTab === "recRedisHealth" && <AdminRecRedisHealthPage />}
    </div>
  );
};

export default AdminSettingsPage;
